import React, { useEffect, useRef, useState } from "react";
import { Box, CloseButton, Flex, Text, Textarea } from "@chakra-ui/react";
import useGetOrderSheetSetInquiries from "@/features/my-order/features/order-history/hooks/useGetOrderSheetSetInquiries";
import useCreateOrderSheetSetInquiry from "@/features/my-order/features/order-history/hooks/useCreateOrderSheetSetInquiry";
import useMarkOrderSheetSetInquiriesRead from "@/features/my-order/features/order-history/hooks/useMarkOrderSheetSetInquiriesRead";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppButton from "@/features/line-sheet-sets/app-button";
import {
  OrderSheetSetInquiry,
  OrderSheetSetInquiryAttachment,
} from "@/features/my-order/features/order-history/types";
import useUploadUnoFile from "@/hooks/use-upload-uno-file";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";

interface OrderHistoryInquiryContentProps {
  orderSheetSetId: number | null;
  // true면 부모 컨테이너 높이를 채우고 입력 영역을 하단 고정한다 (스크롤은 메시지 리스트 하나만).
  // false(기본)면 기존 동작: 리스트가 최대 480px까지 늘어나며 자체 스크롤.
  fillHeight?: boolean;
}

function OrderHistoryInquiryContent({
  orderSheetSetId,
  fillHeight = false,
}: OrderHistoryInquiryContentProps) {
  const numericOrderSheetSetId = orderSheetSetId;
  const identity = useIdentity();
  const { t } = useI18nHelper();

  const { data } = useGetOrderSheetSetInquiries(numericOrderSheetSetId);
  const { createInquiry, isPending } = useCreateOrderSheetSetInquiry(
    numericOrderSheetSetId
  );
  const { markRead } = useMarkOrderSheetSetInquiriesRead(
    numericOrderSheetSetId
  );

  const inquiries = data?.inquiries ?? [];
  const unreadCount = data?.unread_count ?? 0;
  const [draft, setDraft] = useState("");
  // 보내기 전 첨부할 파일 — 등록할 때 /uno-files 로 올리고 그 id 를 함께 보낸다
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: uploadUnoFile } = useUploadUnoFile();
  const { error: showError } = useAppToasts();

  // 처음 로드 시(그리고 메시지가 추가될 때) 목록을 최하단으로 스크롤해 최신 메시지를 보여준다.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [inquiries.length]);

  // 문의 영역을 열거나, 포커스 복귀 등으로 새 메시지를 다시 불러왔을 때 서버에 읽음 처리.
  // 1회 가드 대신 "마지막으로 읽음 처리한 id"를 추적해, 최신 메시지 id가 늘어나고 안 읽은 게 있으면 다시 처리한다.
  // 의도적으로 목록을 즉시 refetch하지 않아 이번 화면에서는 unread 강조가 유지되고, 다음 갱신 때 반영된다.
  const latestInquiryId = inquiries.length
    ? inquiries[inquiries.length - 1].id
    : 0;
  const lastMarkedIdRef = useRef(0);
  useEffect(() => {
    if (!numericOrderSheetSetId) return;
    if (unreadCount > 0 && latestInquiryId > lastMarkedIdRef.current) {
      lastMarkedIdRef.current = latestInquiryId;
      markRead();
    }
  }, [numericOrderSheetSetId, unreadCount, latestInquiryId, markRead]);

  const myCompanyType = identity?.company?.type;
  const myCompanyId = identity?.company?.id;

  const isBusy = isPending || isUploading;
  const canSubmit =
    Boolean(draft.trim() || files.length) &&
    !isBusy &&
    Boolean(numericOrderSheetSetId);

  const handleSubmit = async () => {
    const body = draft.trim();
    if ((!body && files.length === 0) || !numericOrderSheetSetId) return;
    let unoFileIds: number[] = [];
    if (files.length > 0) {
      setIsUploading(true);
      try {
        for (const file of files) {
          const response = await uploadUnoFile(file);
          unoFileIds = [...unoFileIds, response.data.id];
        }
      } catch (e) {
        showError(e);
        return;
      } finally {
        setIsUploading(false);
      }
    }
    createInquiry(
      { body, uno_file_ids: unoFileIds },
      {
        onSuccess: () => {
          setDraft("");
          setFiles([]);
        },
      }
    );
  };

  return (
    <Flex
      flexDirection={"column"}
      gap={"16px"}
      {...(fillHeight ? { flex: 1, minH: 0 } : {})}
    >
      <Flex
        ref={listRef}
        flexDirection={"column"}
        gap={"12px"}
        overflowY={"auto"}
        overscrollBehavior={"contain"}
        pr={"4px"}
        {...(fillHeight ? { flex: 1, minH: 0 } : { maxH: "480px" })}
      >
        {inquiries.length === 0 && (
          <Text textStyle={"body-md-regular"} color={"#8F8F8C"}>
            {t("order_history:inquiry.empty")}
          </Text>
        )}
        {inquiries.map((inquiry) => (
          <InquiryItem
            key={inquiry.id}
            inquiry={inquiry}
            isMine={
              inquiry.author_company_type === myCompanyType &&
              inquiry.author_company_id === myCompanyId
            }
          />
        ))}
      </Flex>

      <Flex flexDirection={"column"} gap={"8px"} flexShrink={0}>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t(
            myCompanyType === "AGENCY"
              ? "order_history:inquiry.placeholder_agency"
              : myCompanyType === "BOUTIQUE"
              ? "order_history:inquiry.placeholder_boutique"
              : "order_history:inquiry.placeholder_buyer"
          )}
          resize={"vertical"}
          minHeight={"80px"}
        />
        {files.length > 0 && (
          <Flex flexDirection={"column"} gap={"4px"}>
            {files.map((file, index) => (
              <Flex
                key={`${file.name}_${index}`}
                alignItems={"center"}
                gap={"4px"}
                fontSize={"12px"}
                color={"#444444"}
              >
                <Text noOfLines={1} flex={1}>
                  {`📎 ${file.name}`}
                </Text>
                <CloseButton
                  size={"sm"}
                  aria-label={"remove attachment"}
                  isDisabled={isBusy}
                  onClick={() =>
                    setFiles((prev) => prev.filter((_, i) => i !== index))
                  }
                />
              </Flex>
            ))}
          </Flex>
        )}
        <input
          ref={fileInputRef}
          type={"file"}
          multiple={true}
          hidden={true}
          data-testid={"inquiry_file_input"}
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            if (picked.length) {
              setFiles((prev) => [...prev, ...picked]);
            }
            e.target.value = "";
          }}
        />
        <Flex justifyContent={"space-between"} gap={"8px"}>
          <AppButton
            variant={"white"}
            isDisabled={isBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            {t("order_history:inquiry.attach")}
          </AppButton>
          <AppButton onClick={handleSubmit} isDisabled={!canSubmit}>
            {t("order_history:inquiry.submit")}
          </AppButton>
        </Flex>
      </Flex>
    </Flex>
  );
}

interface InquiryItemProps {
  inquiry: OrderSheetSetInquiry;
  isMine: boolean;
}

function InquiryItem({ inquiry, isMine }: InquiryItemProps) {
  const { t, tLocalDateTimeString2 } = useI18nHelper();

  const roleLabel =
    inquiry.author_company_type === "BUYER"
      ? t("order_history:inquiry.role_buyer")
      : inquiry.author_company_type === "AGENCY"
      ? t("order_history:inquiry.role_agency")
      : inquiry.author_company_type === "BOUTIQUE"
      ? t("order_history:inquiry.role_boutique")
      : inquiry.author_company_type;

  // 역할 옆에는 쓴 사람이 아니라 회사 이름을 보여준다(예: 부티크 KR4)
  const authorName = inquiry.author_company_name || "";
  const isUnread = !isMine && Boolean(inquiry.is_unread);

  return (
    <Flex
      justifyContent={isMine ? "flex-end" : "flex-start"}
      alignItems={"flex-end"}
      gap={"6px"}
    >
      {/* 상대가 보낸 안 읽은 메시지: 왼쪽에 강조 점 */}
      {isUnread && (
        <Box
          flexShrink={0}
          width={"6px"}
          height={"6px"}
          borderRadius={"full"}
          background={"#FF6B35"}
          alignSelf={"center"}
        />
      )}
      {/* 내가 보낸 메시지: 말풍선 왼쪽 하단에 읽음 여부 */}
      {isMine && (
        <Text
          flexShrink={0}
          fontSize={"10px"}
          whiteSpace={"nowrap"}
          color={inquiry.read_by_counterpart ? "#48B5FE" : "#B0B0AD"}
        >
          {inquiry.read_by_counterpart
            ? t("order_history:inquiry.read")
            : t("order_history:inquiry.unread")}
        </Text>
      )}
      <Box
        maxWidth={"75%"}
        background={isUnread ? "#FFF4EC" : isMine ? "#F0F4FF" : "#F7F9FA"}
        borderRadius={"8px"}
        padding={"12px"}
      >
        {inquiry.created_at && (
          <Text
            textStyle={"caption-regular"}
            fontSize={"10px"}
            color={"#8F8F8C"}
          >
            {tLocalDateTimeString2(new Date(inquiry.created_at))}
          </Text>
        )}
        <Flex gap={"6px"} alignItems={"baseline"} marginBottom={"4px"}>
          <Text textStyle={"body-sm-bold"} fontSize={"10px"} color={"#444444"}>
            {roleLabel}
          </Text>
          {authorName && (
            <Text
              textStyle={"caption-regular"}
              fontSize={"10px"}
              color={"#444444"}
            >
              {authorName}
            </Text>
          )}
        </Flex>
        {inquiry.body && (
          <Text textStyle={"body-md-regular"} whiteSpace={"pre-wrap"}>
            {inquiry.body}
          </Text>
        )}
        {(inquiry.attachments ?? []).length > 0 && (
          <Flex flexDirection={"column"} gap={"2px"} marginTop={"6px"}>
            {(inquiry.attachments ?? []).map((attachment) => (
              <InquiryAttachmentLink
                key={attachment.id}
                attachment={attachment}
              />
            ))}
          </Flex>
        )}
      </Box>
    </Flex>
  );
}

// 첨부파일 — 누르면 서명된 주소를 받아 내려받는다
function InquiryAttachmentLink({
  attachment,
}: {
  attachment: OrderSheetSetInquiryAttachment;
}) {
  const { error: showError } = useAppToasts();
  const { fire, isLoading, data, error, clear } = useDownloadUnoFile();

  useEffect(() => {
    if (data) {
      clear();
    }
  }, [data, clear]);

  useEffect(() => {
    if (error) {
      showError(error);
      clear();
    }
  }, [error, showError, clear]);

  return (
    <Text
      as={"button"}
      type={"button"}
      textAlign={"left"}
      fontSize={"12px"}
      color={"#1272EF"}
      _hover={{ textDecoration: "underline" }}
      opacity={isLoading ? 0.5 : 1}
      onClick={() => fire({ unoFileId: attachment.id }, {})}
    >
      {`📎 ${attachment.name}`}
    </Text>
  );
}

export default OrderHistoryInquiryContent;
